import type { ComponentType, SVGProps } from 'react'
import { useLingui } from '@lingui/react/macro'
import { LanguagesIcon, CheckIcon } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { useTranslation } from '@/lib/locales'
import { cn } from '@/lib/utils'
import { SpainFlag, UKFlag, CatalanFlag, BrazilFlag } from '@/components/custom/country-flags'
import { ButtonPrimitive } from '@/components/ui/button-primitive'

const languages: { code: string; name: string; Flag: ComponentType<SVGProps<SVGSVGElement>> }[] = [
  { code: 'es-ES', name: 'Español', Flag: SpainFlag },
  { code: 'en-US', name: 'English', Flag: UKFlag },
  { code: 'ca-ES', name: 'Català', Flag: CatalanFlag },
  { code: 'pt-BR', name: 'Português', Flag: BrazilFlag },
]

// Controlled dialog — opened from a menu item in user-menu.tsx, rendered outside the
// dropdown to avoid the menu↔dialog modal open/close race. On mobile it is opened from INSIDE the user-menu
// sheet, which stays visible behind it: `forceBackdrop` is what dims and blurs that sheet (base-ui renders no
// backdrop for a nested dialog by default). Harmless on the dropdown, where the dialog isn't nested.
export function ChangeLanguageDialog(props: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useLingui()
  const { language, changeLanguage } = useTranslation()

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent forceBackdrop className="flex flex-col gap-2 sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LanguagesIcon className="text-primary size-5" />
            {t`Change language`}
          </DialogTitle>
          <DialogDescription>{t`Select your preferred language`}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          {languages.map((lang) => {
            const isSelected = language === lang.code
            return (
              <ButtonPrimitive
                type="button"
                key={lang.code}
                onClick={() => {
                  changeLanguage(lang.code)
                  props.onOpenChange(false)
                }}
                className={cn(
                  'group flex cursor-pointer items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition-colors',
                  isSelected
                    ? 'border-primary/30 bg-primary/5'
                    : 'border-transparent bg-muted/30 hover:border-muted-foreground/20 hover:bg-muted/50',
                )}
              >
                <lang.Flag className="size-6 shrink-0 rounded-sm" />
                <span
                  className={cn('flex-1 text-sm font-medium', isSelected ? 'text-foreground' : 'text-muted-foreground')}
                >
                  {lang.name}
                </span>
                {isSelected && (
                  <div className="bg-primary flex size-5 items-center justify-center rounded-full">
                    <CheckIcon className="text-primary-foreground size-3" />
                  </div>
                )}
              </ButtonPrimitive>
            )
          })}
        </div>
      </DialogContent>
    </Dialog>
  )
}
